import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  AutoAwesome as AutoAwesomeIcon,
  SupportAgent as SupportAgentIcon,
  RocketLaunch as RocketLaunchIcon,
  Psychology as PsychologyIcon,
  Campaign as CampaignIcon,
  HeadsetMic as HeadsetMicIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Engineering as EngineeringIcon,
  AccountBalance as AccountBalanceIcon,
  Restaurant as RestaurantIcon,
  Home as HomeIcon,
  DirectionsCar as DirectionsCarIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  Tune as TuneIcon,
  Palette as PaletteIcon,
  Brush as BrushIcon,
  Create as CreateIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeOff as VolumeOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  CameraAlt as CameraAltIcon,
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  MusicNote as MusicNoteIcon,
  LibraryMusic as LibraryMusicIcon,
  Movie as MovieIcon,
  Theaters as TheatersIcon,
  Sports as SportsIcon,
  FitnessCenter as FitnessCenterIcon,
  Pool as PoolIcon,
  DirectionsRun as DirectionsRunIcon,
  DirectionsBike as DirectionsBikeIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Flight as FlightIcon,
  Train as TrainIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
  Hotel as HotelIcon,
  LocationOn as LocationOnIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Map as MapIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  GTranslate as GTranslateIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  MoreHoriz as MoreHorizIcon,
  Apps as AppsIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  ViewStream as ViewStreamIcon,
  ViewColumn as ViewColumnIcon,
  ViewWeek as ViewWeekIcon,
  ViewDay as ViewDayIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewCarousel as ViewCarouselIcon,
  ViewQuilt as ViewQuiltIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewInAr as ViewInArIcon,
  ViewArray as ViewArrayIcon,
  ViewCompact as ViewCompactIcon,
  ViewHeadline as ViewHeadlineIcon,
  ViewKanban as ViewKanbanIcon,
  ViewTimeline as ViewTimelineIcon,
} from '@mui/icons-material';
import { Template } from '../../utils/templateApi';

type TemplateViewDialogProps = {
  open: boolean;
  onClose: () => void;
  template: Template | null;
  onEdit?: (template: Template) => void;
  onDuplicate?: (template: Template) => void;
  onDelete?: (template: Template) => void;
};

// Icon mapping for display
const iconMap: Record<string, React.ComponentType<any>> = {
  AutoAwesome: AutoAwesomeIcon,
  SupportAgent: SupportAgentIcon,
  RocketLaunch: RocketLaunchIcon,
  Psychology: PsychologyIcon,
  Campaign: CampaignIcon,
  HeadsetMic: HeadsetMicIcon,
  ShoppingCart: ShoppingCartIcon,
  Business: BusinessIcon,
  School: SchoolIcon,
  HealthAndSafety: HealthAndSafetyIcon,
  Engineering: EngineeringIcon,
  AccountBalance: AccountBalanceIcon,
  Restaurant: RestaurantIcon,
  Home: HomeIcon,
  DirectionsCar: DirectionsCarIcon,
  Computer: ComputerIcon,
  Phone: PhoneIcon,
  Email: EmailIcon,
  Chat: ChatIcon,
  Forum: ForumIcon,
  QuestionAnswer: QuestionAnswerIcon,
  Help: HelpIcon,
  Info: InfoIcon,
  Warning: WarningIcon,
  Error: ErrorIcon,
  CheckCircle: CheckCircleIcon,
  Cancel: CancelIcon,
  Star: StarIcon,
  Favorite: FavoriteIcon,
  ThumbUp: ThumbUpIcon,
  ThumbDown: ThumbDownIcon,
  Share: ShareIcon,
  Download: DownloadIcon,
  Upload: UploadIcon,
  Settings: SettingsIcon,
  Build: BuildIcon,
  Tune: TuneIcon,
  Palette: PaletteIcon,
  Brush: BrushIcon,
  Create: CreateIcon,
  Print: PrintIcon,
  Save: SaveIcon,
  CloudUpload: CloudUploadIcon,
  CloudDownload: CloudDownloadIcon,
  Sync: SyncIcon,
  Refresh: RefreshIcon,
  RestartAlt: RestartAltIcon,
  PlayArrow: PlayArrowIcon,
  Pause: PauseIcon,
  Stop: StopIcon,
  SkipNext: SkipNextIcon,
  SkipPrevious: SkipPreviousIcon,
  FastForward: FastForwardIcon,
  FastRewind: FastRewindIcon,
  VolumeUp: VolumeUpIcon,
  VolumeDown: VolumeDownIcon,
  VolumeOff: VolumeOffIcon,
  Mic: MicIcon,
  MicOff: MicOffIcon,
  Videocam: VideocamIcon,
  VideocamOff: VideocamOffIcon,
  CameraAlt: CameraAltIcon,
  PhotoCamera: PhotoCameraIcon,
  Image: ImageIcon,
  VideoLibrary: VideoLibraryIcon,
  MusicNote: MusicNoteIcon,
  LibraryMusic: LibraryMusicIcon,
  Movie: MovieIcon,
  Theaters: TheatersIcon,
  Sports: SportsIcon,
  FitnessCenter: FitnessCenterIcon,
  Pool: PoolIcon,
  DirectionsRun: DirectionsRunIcon,
  DirectionsBike: DirectionsBikeIcon,
  DirectionsWalk: DirectionsWalkIcon,
  Flight: FlightIcon,
  Train: TrainIcon,
  DirectionsBus: DirectionsBusIcon,
  LocalTaxi: LocalTaxiIcon,
  Hotel: HotelIcon,
  LocationOn: LocationOnIcon,
  MyLocation: MyLocationIcon,
  Navigation: NavigationIcon,
  Map: MapIcon,
  Public: PublicIcon,
  Language: LanguageIcon,
  Translate: TranslateIcon,
  GTranslate: GTranslateIcon,
  Menu: MenuIcon,
  MoreVert: MoreVertIcon,
  MoreHoriz: MoreHorizIcon,
  Apps: AppsIcon,
  GridView: GridViewIcon,
  ViewList: ViewListIcon,
  ViewModule: ViewModuleIcon,
  ViewComfy: ViewComfyIcon,
  ViewStream: ViewStreamIcon,
  ViewColumn: ViewColumnIcon,
  ViewWeek: ViewWeekIcon,
  ViewDay: ViewDayIcon,
  ViewAgenda: ViewAgendaIcon,
  ViewCarousel: ViewCarouselIcon,
  ViewQuilt: ViewQuiltIcon,
  ViewSidebar: ViewSidebarIcon,
  ViewInAr: ViewInArIcon,
  ViewArray: ViewArrayIcon,
  ViewCompact: ViewCompactIcon,
  ViewHeadline: ViewHeadlineIcon,
  ViewKanban: ViewKanbanIcon,
  ViewTimeline: ViewTimelineIcon,
};

const TemplateViewDialog: React.FC<TemplateViewDialogProps> = ({
  open,
  onClose,
  template,
  onEdit,
  onDuplicate,
  onDelete
}) => {
  const theme = useTheme();

  if (!template) return null;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Lead': return 'primary';
      case 'Assistente': return 'secondary';
      case 'Ambos': return 'success';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'Enabled' ? 'success' : 'error';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {template.name}
        </Typography>
        <Box>
          {onEdit && (
            <IconButton onClick={() => onEdit(template)} size="small" sx={{ mr: 1 }}>
              <EditIcon />
            </IconButton>
          )}
          {onDuplicate && (
            <IconButton onClick={() => onDuplicate(template)} size="small" sx={{ mr: 1 }}>
              <CopyIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton onClick={() => onDelete(template)} size="small" sx={{ mr: 1 }} color="error">
              <DeleteIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Informações Básicas</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Nome</Typography>
                <Typography variant="body1">{template.name}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Descrição</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {template.description}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip 
                  label={template.type} 
                  sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                  // color={getTypeColor(template.type) as any}
                  size="small"
                />
                <Chip 
                  label={template.status} 
                  color={getStatusColor(template.status) as any}
                  variant="outlined"
                  size="small"
                />
                {template.category && (
                  <Chip 
                    label={template.category} 
                    variant="outlined"
                    size="small"
                  />
                )}
                {template.isDefault && (
                  <Chip 
                    label="Padrão" 
                    color="info"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Configuration */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Configuração</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Modelo</Typography>
                <Typography variant="body1">{template.model}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Delay de Resposta</Typography>
                <Typography variant="body1">{template.responseDelay}ms</Typography>
              </Box>
              
              {template.icon && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Ícone</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {iconMap[template.icon] && (
                      React.createElement(iconMap[template.icon], { fontSize: "small", color: "primary" })
                    )}
                    <Typography variant="body1">{template.icon}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Prompts */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Prompts</Typography>
            {template.prompts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum prompt configurado
              </Typography>
            ) : (
              <List>
                {template.prompts.map((prompt, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip 
                            label={prompt.role} 
                            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                            size="small" 
                          />
                        </Box>
                      }
                      secondary={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace',
                            backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                            padding: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.200',
                            color: theme.palette.mode === 'dark' ? 'text.primary' : undefined
                          }}
                        >
                          {prompt.content}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Fechar
        </Button>
        {onEdit && (
          <Button onClick={() => onEdit(template)} variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} startIcon={<EditIcon />}>
            Editar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TemplateViewDialog;
